// client/src/components/company/ContactBank.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Mail, Building2, X, CheckCircle, Info } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const ContactBank = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [sending, setSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [message, setMessage] = useState('');
  const [messageError, setMessageError] = useState('');
  const navigate = useNavigate();

  // Helper function to remove "Spa" suffix from bank name
  const formatBankName = (bankName) => {
    if (!bankName) return '';
    return bankName.replace(/\s+Spa$/i, '').trim();
  };

  useEffect(() => {
    fetchNonReferralBanks();
  }, []);

  const fetchNonReferralBanks = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/contact-bank/non-referral-banks');
      setBanks(res.data.banks.filter(bank => bank.bankName !== 'Banca Demo EO'));
      setError('');
    } catch (err) {
      console.error('Error fetching banks:', err);
      setError(t('contactBank.errorFetchingBanks') || 'Errore nel recuperare le banche');
    } finally {
      setLoading(false);
    }
  };

  const handleContactClick = (bank) => {
    setSelectedBank(bank);
    setShowModal(true);
    setSuccessMessage('');
    setMessageError('');
  };

  const handleConfirmContact = async () => {
    if (!selectedBank) return;

    // Validazione: il campo dettagli è obbligatorio
    if (!message.trim()) {
      setMessageError(t('contactBank.messageRequired') || 'Campo obbligatorio');
      return;
    }

    try {
      setSending(true);
      setMessageError('');
      await axios.post('/api/contact-bank/send', {
        bankId: selectedBank._id,
        bankName: selectedBank.bankName,
        message: message.trim()
      });

      setSuccessMessage(t('contactBank.emailSentSuccess') || 'Email inviata con successo!');
      setShowModal(false);
      setMessage('');
      setMessageError('');

      // Rimuovi il messaggio di successo dopo 5 secondi
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (err) {
      console.error('Error sending contact email:', err);
      setError(t('contactBank.errorSendingEmail') || 'Errore nell\'invio dell\'email');
    } finally {
      setSending(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedBank(null);
    setMessage('');
    setMessageError('');
  };

  return (
    <div className="company-dashboard">
      <div className="card">
        <div className="card-title">
          <Mail size={20} className="card-title-icon" />
          <span>{t('contactBank.title') || 'Contatta una Banca'}</span>
        </div>
        
        <p style={{ color: 'var(--grey)', marginBottom: '20px', fontSize: '14px' }}>
          {t('contactBank.subtitle') || 'Seleziona una banca per richiedere di essere contattato'}
        </p>

        {/* Success Message */}
        {successMessage && (
          <div className="alert-success">
            <CheckCircle size={20} style={{ marginRight: '10px', verticalAlign: 'middle' }} />
            <span>{successMessage}</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="alert-danger">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--grey)' }}>
            <p>{t('common.loading') || 'Caricamento in corso...'}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && banks.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Building2 size={48} style={{ color: 'var(--grey)', marginBottom: '15px' }} />
            <p style={{ color: 'var(--grey)' }}>
              {t('contactBank.noBanksAvailable') || 'Nessuna banca disponibile al momento'}
            </p>
          </div>
        )}

        {/* Banks List */}
        {!loading && banks.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            {banks.map((bank) => (
              <div 
                key={bank._id} 
                className="checkbox-label"
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '15px',
                  marginBottom: '10px',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'default'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <Building2 size={24} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '16px', color: 'var(--dark)' }}>
                      {formatBankName(bank.bankName)}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', marginLeft: '15px', flexShrink: 0 }}>
                    <button
                        onClick={() => navigate(`/company/bank-info/${encodeURIComponent(bank.bankName)}`)}
                        className="btn btn-secondary"
                        style={{ 
                        padding: '8px 20px',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: 'white',
                        color: 'var(--primary)',
                        border: '2px solid var(--primary)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500'
                        }}
                    >
                        <Info size={16} />
                        {t('contactBank.infoButton') || 'Info'}
                    </button>
                    <button
                        onClick={() => handleContactClick(bank)}
                        className="btn btn-primary"
                        style={{ 
                        padding: '8px 20px',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                        }}
                        disabled={sending}
                    >
                        <Mail size={16} />
                        {t('contactBank.contactButton') || 'Contatta'}
                    </button>
                    </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showModal && selectedBank && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>{t('contactBank.confirmTitle') || 'Conferma Invio'}</h3>
              <button
                className="close-btn"
                onClick={handleCloseModal}
                disabled={sending}
              >
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ 
                fontSize: '16px', 
                color: 'var(--dark)', 
                marginBottom: '20px', 
                lineHeight: '1.5' 
              }}>
                {t('contactBank.confirmMessage') || 
                  'Verrà inviata una mail a tutti i referenti della banca, sei sicuro di voler procedere?'}
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                padding: '15px',
                backgroundColor: 'var(--light-grey)',
                borderRadius: '6px',
                border: '1px solid var(--border)'
              }}>
                <Building2 size={24} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                <div>
                  <strong style={{ display: 'block', fontSize: '16px', color: 'var(--dark)' }}>
                    {formatBankName(selectedBank.bankName)}
                  </strong>
                </div>
              </div>
              <div style={{ marginTop: '20px' }}>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: 'var(--dark)',
                  marginBottom: '8px'
                }}>
                  {t('contactBank.detailsLabel') || 'Dettagli'} <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <textarea
                  className="form-control"
                  placeholder={t('contactBank.messagePlaceholder') || 'Inserisci un messaggio'}
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    if (messageError && e.target.value.trim()) {
                      setMessageError('');
                    }
                  }}
                  rows={4}
                  maxLength={500}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    borderRadius: '6px',
                    border: messageError ? '1px solid var(--danger)' : '1px solid var(--border)',
                    resize: 'vertical'
                  }}
                />
                {messageError && (
                  <div style={{
                    color: 'var(--danger)',
                    fontSize: '12px',
                    marginTop: '5px'
                  }}>
                    {messageError}
                  </div>
                )}
              </div>
              <div style={{
                textAlign: 'right',
                fontSize: '12px',
                color: 'var(--grey)',
                marginTop: '5px'
              }}>
                {message.length}/500
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={handleCloseModal}
                disabled={sending}
              >
                {t('common.cancel') || 'Annulla'}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmContact}
                disabled={sending}
              >
                {sending ? (t('common.sending') || 'Invio...') : (t('common.confirm') || 'Conferma')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactBank;